/**
 * Import Monitor — Date Inconsistency / New PO Date Inconsistency exceptions.
 *
 * CR-9031. Open PO lines whose ODD, Customer Required Date and Supplier Promise
 * Date disagree: `accepted` after the customer accepted a supplier proposal,
 * `new` while the line is still New.
 */
import api from './client';

export type DateInconsistencyKind = 'accepted' | 'new';

export interface DateInconsistencyRow {
  /** PurchaseOrderLine primary key, for the PO line link. */
  po_line_id: number;
  po_number: string;
  line_number: number;
  supplier_name: string | null;
  /** ODD. */
  original_required_date: string | null;
  customer_required_date: string;
  supplier_promise_date: string | null;
}

export interface DateInconsistencyResponse {
  kind: DateInconsistencyKind;
  count: number;
  page: number;
  page_size: number;
  results: DateInconsistencyRow[];
}

function toParams(customerId: number, kind: DateInconsistencyKind, search?: string): Record<string, string | number> {
  const params: Record<string, string | number> = { customer_id: customerId, kind };
  if (search) params['search'] = search;
  return params;
}

export const dateInconsistencyAPI = {
  /** One page of exception lines. */
  list: (
    customerId: number,
    kind: DateInconsistencyKind,
    search: string | undefined,
    page: number,
    pageSize: number,
  ): Promise<DateInconsistencyResponse> =>
    api.get<DateInconsistencyResponse>('/imports/date-inconsistency/', {
      params: { ...toParams(customerId, kind, search), page, page_size: pageSize },
    }).then(r => r.data),

  /** Every matching line, as an .xlsx blob. */
  exportXlsx: (customerId: number, kind: DateInconsistencyKind, search?: string): Promise<Blob> =>
    api.get('/imports/date-inconsistency/export/', {
      params: toParams(customerId, kind, search),
      responseType: 'blob',
    }).then(r => r.data as Blob),
};
